import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getStudentAssignments } from '../../services/assignmentService';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import { isPast, isToday } from '../../utils/dateUtils';
import { getAssignmentDisplayInfo } from '../../utils/assignmentUtils';
import '../../styles/StudentAssignmentsListPage.css';

/**
 * StudentAssignmentsListPage - Assignment list view for STUDENTS and PARENTS
 * 
 * Used by:
 * - Students: Viewing their own assignments via /assignments/student
 * - Parents: Viewing their child's assignments when clicking "View all" from ChildProfilePage
 * 
 * Features:
 * - Tabbed interface (All, Pending, Assessed, Overdue)
 * - Shows all assignments across all classes (no class filtering)
 * - Read-only view (no edit/delete actions)
 * 
 * Different from AssignmentsListPage which is used by TEACHERS for managing assignments
 */
const StudentAssignmentsListPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Get childId from location state if this is a parent viewing a child's assignments
  const childId = location.state?.childId;

  useEffect(() => {
    fetchAssignments();
  }, []);
  
  // Monitor filter changes for debugging
  useEffect(() => {
    console.log('Active filter changed to:', activeFilter);
  }, [activeFilter]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      // Determine if we're fetching for the logged-in student or a child (for parent)
      let data;
      if (childId) {
        // Parent viewing a child's assignments
        console.log('Fetching assignments for child ID:', childId);
        // For parents viewing child assignments, use the parent-specific endpoint
        try {
          const response = await api.get(`/parents/children/${childId}/assignments`);
          if (response.data && response.data.data) {
            data = response.data.data;
            console.log('Fetched child assignments via parent API:', data);
          } else {
            console.warn('Unexpected response format from parent API:', response.data);
            data = [];
          }
        } catch (err) {
          console.error('Error fetching child assignments via parent API:', err);
          // Fall back to regular student endpoint if parent-specific fails
          data = await getStudentAssignments(childId);
        }
      } else {
        // Student viewing their own assignments
        data = await getStudentAssignments();
      }
      console.log('Fetched assignments:', data);
      
      // Handle different response formats
      let assignmentsArray = [];
      
      if (Array.isArray(data)) {
        assignmentsArray = data;
      } else if (data && Array.isArray(data.data)) {
        assignmentsArray = data.data;
      } else if (data && data.assignments && Array.isArray(data.assignments)) {
        assignmentsArray = data.assignments;
      } else {
        console.error('Expected array of assignments but got:', data);
        assignmentsArray = [];
      }
      
      // Filter out null or undefined assignments
      assignmentsArray = assignmentsArray.filter(assignment => assignment);
      
      console.log('Processing assignments:', assignmentsArray.length);
      
      // Process assignments to ensure they have required fields
      const processedAssignments = assignmentsArray.map(assignment => {
        // Determine assessment status
        const hasAssessment = assignment.assessment && 
          (assignment.assessment.rating || assignment.assessment.comments);
          
        // Determine practice status
        const isPracticed = 
          assignment.isPracticed || 
          (assignment.practiceLogs && assignment.practiceLogs.length > 0) ||
          assignment.status === 'completed';
          
        // Ensure critical fields exist - use utility to handle both legacy and new formats
        return {
          ...assignment,
          _id: assignment._id || assignment.id || `temp-${Math.random()}`,
          type: assignment.type || 'new-memorization',
          status: assignment.status || 'pending',
          dueDate: assignment.dueDate || new Date().toISOString(),
          hasAssessment: hasAssessment,
          isPracticed: isPracticed
        };
      });
      
      console.log('Processed assignments:', processedAssignments);
      
      // Log date fields for completed assignments to debug
      const completedAssignments = processedAssignments.filter(a => a.status === 'completed');
      if (completedAssignments.length > 0) {
        console.log('Completed assignments date fields:');
        completedAssignments.forEach(assignment => {
          console.log(`Assignment ID ${assignment._id}:`, {
            completedDate: assignment.completedDate,
            updatedAt: assignment.updatedAt,
            createdAt: assignment.createdAt,
            assessedAt: assignment.assessment?.assessedAt,
            assessmentDate: assignment.assessment?.date
          });
        });
      }
      
      // Sort assignments to show newest first (consistent with teacher view)
      const sortedAssignments = processedAssignments.sort((a, b) => {
        // Determine the most relevant date for each assignment
        // For completed assignments, prefer assessment date, then completed date
        // For pending assignments, use due date or created date
        const getRelevantDate = (assignment) => {
          if (assignment.status === 'completed' && assignment.assessment?.assessedAt) {
            return new Date(assignment.assessment.assessedAt);
          }
          if (assignment.completedDate) {
            return new Date(assignment.completedDate);
          }
          if (assignment.updatedAt) {
            return new Date(assignment.updatedAt);
          }
          if (assignment.createdAt) {
            return new Date(assignment.createdAt);
          }
          return new Date(0); // Fallback to epoch if no date available
        };
        
        const dateA = getRelevantDate(a);
        const dateB = getRelevantDate(b);
        
        // Sort in descending order (newest first)
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('Assignments sorted - newest first');
      setAssignments(sortedAssignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = (filter) => {
    setActiveFilter(filter);
  };

  const getFilteredAssignments = () => {
    // Ensure assignments is an array with valid items
    const validAssignments = Array.isArray(assignments) ? assignments.filter(a => a) : [];
    
    // Log for debugging purposes
    console.log('All assignments:', validAssignments);
    
    if (activeFilter === 'all') return validAssignments;
    
    if (activeFilter === 'pending') {
      const pendingAssignments = validAssignments.filter(assignment => {
        // Include assignments that are active/pending and not overdue
        const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
        const isOverdue = dueDate && isPast(assignment.dueDate);
        const isPending = assignment.status === 'active' || 
                         assignment.status === 'pending' || 
                         !assignment.status;
        
        return isPending && !isOverdue;
      });
      console.log('Pending assignments:', pendingAssignments);
      return pendingAssignments;
    }
    
    if (activeFilter === 'assessed') {
      const assessedAssignments = validAssignments.filter(assignment => {
        // Assignment has been assessed if it has an assessment object with a rating
        // OR if it has a status of 'completed'
        return (assignment.assessment && assignment.assessment.rating) || 
               (assignment.status === 'completed');
      });
      console.log('Assessed assignments:', assessedAssignments);
      return assessedAssignments;
    }
    
    if (activeFilter === 'overdue') {
      const overdueAssignments = validAssignments.filter(assignment => {
        // Assignment is overdue if due date is in the past and status is not completed
        const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
        return dueDate && 
               isPast(assignment.dueDate) && 
               assignment.status !== 'completed';
      });
      console.log('Overdue assignments:', overdueAssignments);
      return overdueAssignments;
    }
    
    return validAssignments;
  };

  const formatDate = (dateString, isCompletionDate = false) => {
    if (!dateString) return isCompletionDate ? 'Unknown date' : 'Not set';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return isCompletionDate ? 'Unknown date' : 'Not set';
      }

      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return isCompletionDate ? 'Unknown date' : dateString;
    }
  };

  const getPlanBadgeColors = (type) => {
    switch(type) {
      case 'revision':
        return { backgroundColor: '#f3e5f5', color: '#7227b0' };
      case 'recitation':
        return { backgroundColor: '#e8f5e9', color: '#1b5e20' };
      case 'new-memorization':
      default:
        return { backgroundColor: '#fef8e8', color: '#f57c00' };
    }
  };

  const renderAssignmentCard = (assignment) => {
    // Check if we have all necessary data
    if (!assignment) {
      console.warn('Attempted to render null or undefined assignment');
      return null;
    }

    // Debug log to understand what's in each assignment
    console.log('Rendering assignment card:', assignment);

    // Use utility to get assignment display info (handles both legacy and new formats)
    const displayInfo = getAssignmentDisplayInfo(assignment);
    const { surahName, versesFrom, versesTo, title } = displayInfo;

    // Determine assignment status flags
    const isOverdue = assignment.dueDate && 
      isPast(assignment.dueDate) && 
      assignment.status !== 'completed';
    const isDueToday = assignment.dueDate && isToday(assignment.dueDate);
    const isPracticed = assignment.isPracticed || 
      (assignment.practiceLogs && assignment.practiceLogs.length > 0);
    const isCompleted = assignment.status === 'completed';
    const isAssessed = assignment.hasAssessment || 
      (assignment.assessment && (assignment.assessment.rating || assignment.assessment.comments));
    
    let statusClass = 'pending';
    let statusLabel = 'Pending';
    
    if (isOverdue) {
      statusClass = 'overdue';
      statusLabel = 'Overdue';
    } else if (isDueToday && !isCompleted) {
      statusClass = 'due-today';
      statusLabel = 'Due Today';
    } else if (isCompleted) {
      statusClass = 'completed';
      statusLabel = 'Completed';
    } else if (isPracticed) {
      statusClass = 'practiced';
      statusLabel = 'Practiced';
    }

    return (
      <Link 
        to={`/assignments/student-view/${assignment._id}`}
        className={`student-assignment-card ${statusClass} ${isAssessed && assignment.assessment && assignment.assessment.comments ? 'has-comments' : ''}`}
        key={assignment._id}
        state={location.state?.fromParentChildView ? {
          fromParentChildView: true,
          childId: childId,
          childName: location.state.childName
        } : null}
      >
        <div className={`card-left-border ${statusClass}`}></div>
        
        <div className="assignment-content">
          <div className="assignment-header">
            <h3 className="assignment-title">
              {title}
            </h3>

            {assignment.planId ? (
              <span style={{
                fontSize: '11px',
                padding: '2px 8px',
                ...getPlanBadgeColors(assignment.type),
                borderRadius: '4px',
                fontWeight: '500',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap'
              }}>
                📋 {assignment.type.replace('-', ' ')} Plan
              </span>
            ) : (
              <div className={`assignment-type-badge type-${assignment.type}`}>
                {assignment.type === 'new-memorization' ? 'New Memorization' :
                 assignment.type === 'revision' ? 'Revision' :
                 assignment.type === 'recitation' ? 'Recitation' : 'Unknown'}
              </div>
            )}
          </div>
          
          <div className="assignment-due-date">
            {isCompleted 
              ? `Completed on ${formatDate(assignment.completedDate || assignment.assessment?.assessedAt || assignment.updatedAt, true)}`
              : `Due: ${formatDate(assignment.dueDate)}`
            }
          </div>
          
          <div className="assignment-status-badges">
            <div className={`status-badge ${statusClass}`}>
              {statusLabel}
            </div>
            
            {isAssessed && assignment.assessment && assignment.assessment.rating && (
              <div className={`assessment-badge ${(assignment.assessment.rating || '').toLowerCase().replace(/\s+/g, '-')}`}>
                {assignment.assessment.rating}
              </div>
            )}

            {isAssessed && assignment.assessment && assignment.assessment.comments && (
              <div className="teacher-comments">
                <div className="comments-label">Teacher comments:</div>
                <div className="comments-text">"{assignment.assessment.comments}"</div>
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="student-assignments-container">
      <div className="assignments-header">
        {location.state?.fromParentChildView ? (
          <Link to={`/parent/children/${childId}`} className="back-link">
            ← Back to Child Profile
          </Link>
        ) : (
          <Link to="/dashboard" className="back-link">
            ← Back to Dashboard
          </Link>
        )}
        <h1>{location.state?.fromParentChildView ? `${location.state.childName || 'Child'}'s Assignments` : 'My Assignments'}</h1>
        <div className="placeholder-div"></div>
      </div>

      <div className="filter-tabs">
        <button 
          className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => filterAssignments('all')}
        >
          All
        </button>
        
        <button 
          className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
          onClick={() => filterAssignments('pending')}
        >
          Pending
        </button>
        
        <button 
          className={`filter-tab ${activeFilter === 'assessed' ? 'active' : ''}`}
          onClick={() => filterAssignments('assessed')}
        >
          Assessed
        </button>
        
        <button 
          className={`filter-tab ${activeFilter === 'overdue' ? 'active' : ''}`}
          onClick={() => filterAssignments('overdue')}
        >
          Overdue
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading assignments...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <h2>Failed to load assignments</h2>
          <p>{error}</p>
          <button onClick={fetchAssignments} className="retry-button">
            Try again
          </button>
        </div>
      ) : getFilteredAssignments().length === 0 ? (
        <div className="empty-state">
          <p>
            {activeFilter === 'all' ? 'No assignments found' :
             activeFilter === 'pending' ? 'No pending assignments' :
             activeFilter === 'assessed' ? 'No assessed assignments' :
             activeFilter === 'overdue' ? 'No overdue assignments' :
             'No assignments match the current filter'}
          </p>
          {activeFilter !== 'all' && (
            <button onClick={() => setActiveFilter('all')} className="view-all-button">
              View all assignments
            </button>
          )}
        </div>
      ) : (
        <div className="assignments-list">
          {getFilteredAssignments().map(assignment => renderAssignmentCard(assignment))}
        </div>
      )}
    </div>
  );
};

export default StudentAssignmentsListPage;